import { useQuery } from '@tanstack/react-query'
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { metricsApi } from '../../api/metrics'
import { Mail, TrendingUp, FolderOpen, CalendarDays } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

const COLORS = ['#7c3aed', '#059669', '#d97706', '#0ea5e9', '#8b5cf6', '#10b981']

export function DashboardKPI() {
  const { data: dashboard, isLoading: dashLoading, error: dashError } = useQuery({
    queryKey: ['metrics-dashboard'],
    queryFn: () => metricsApi.getDashboard(),
  })

  const { data: volume, isLoading: volLoading, error: volError } = useQuery({
    queryKey: ['email-volume'],
    queryFn: () => metricsApi.getEmailVolume(14),
  })

  const { data: replyRate, isLoading: replyLoading, error: replyError } = useQuery({
    queryKey: ['auto-reply-rate'],
    queryFn: () => metricsApi.getAutoReplyRate(),
  })

  const { data: queueStats, error: queueError } = useQuery({
    queryKey: ['queue-stats'],
    queryFn: () => metricsApi.getQueueStats(),
    staleTime: 2 * 60 * 1000,
  })

  const error = dashError || volError || replyError || queueError

  if (error) {
    return (
      <div className="p-8 text-center text-danger">
        <p className="text-base font-semibold">Errore caricamento metriche</p>
        <p className="text-sm mt-2">{error.message}</p>
      </div>
    )
  }

  if (dashLoading || volLoading || replyLoading) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Caricamento metriche...
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Mail className="size-5 text-primary" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Email Oggi</div>
                <div className="text-2xl font-bold text-primary">{dashboard?.emails_today || 0}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-success/10 rounded-lg">
                <TrendingUp className="size-5 text-success" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Email Settimana</div>
                <div className="text-2xl font-bold text-success">{dashboard?.emails_week || 0}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-warning/10 rounded-lg">
                <FolderOpen className="size-5 text-warning" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Casi Aperti</div>
                <div className="text-2xl font-bold text-warning">{dashboard?.open_cases || 0}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-info/10 rounded-lg">
                <CalendarDays className="size-5 text-info" />
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Appuntamenti Oggi</div>
                <div className="text-2xl font-bold text-info">{dashboard?.appointments_today || 0}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Volume Chart */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">Volume Email (14 giorni)</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={volume || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-background)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius)',
                    fontSize: '12px',
                  }}
                />
                <Line type="monotone" dataKey="count" stroke="var(--color-primary)" strokeWidth={2} name="Email" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">Distribuzione Categorie</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={dashboard?.emails_by_category || []}
                  dataKey="count"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ category, count }) => `${category}: ${count}`}
                >
                  {(dashboard?.emails_by_category || []).map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-background)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius)',
                    fontSize: '12px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Auto Reply Rate */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Tasso Auto-Reply</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Totale Bozze</div>
              <div className="text-2xl font-bold text-foreground">{replyRate?.total_drafts || 0}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Approvate</div>
              <div className="text-2xl font-bold text-success">{replyRate?.approved || 0}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Tasso Approvazione</div>
              <div className="text-2xl font-bold text-primary">{replyRate?.approval_rate || 0}%</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Imaging Accuracy */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Analisi Imaging</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Analisi Totali</div>
              <div className="text-2xl font-bold text-foreground">{dashboard?.total_imaging_analyses || 0}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Accuracy Media</div>
              <div className="text-2xl font-bold text-success">{dashboard?.avg_imaging_accuracy || 0}%</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Queue Stats */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-foreground mb-4">Statistiche Coda</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Non Assegnati</div>
              <div className="text-2xl font-bold text-warning">{queueStats?.unassigned || 0}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">In Attesa</div>
              <div className="text-2xl font-bold text-info">{queueStats?.pending || 0}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
